import {createStore} from "zustand/vanilla";
import {immer} from "zustand/middleware/immer";
import {useStore} from "zustand";

interface Car {
  driverId: string | null,
  driverBirthDate: Date,
  driverHasCreditCard: boolean,
  bookedEquipmentsWithQuantity: {
    id: string,
    quantity: number
  }[],
  isEditing: boolean
}

interface CarRentalFilteringServiceState {
  cars: Record<string, Car>,
  editingCarsList: string[],
  isCarBeingUpgraded: boolean,
  isShowingExtraCars: boolean,
  isShowingFilterableCars: boolean,
  upgradedCar: string
  selectedCar: string
}

interface CarRentalFilteringServiceStoreActions {
  startEditingCar: (carId: string, carState: Car) => void;
  endEditingCar: (carId: string) => void;
  endEditingCarsExcept: (carIds: string[]) => void
  setDriverId: (carId: string, driverId: string | null) => void;
  setDriverBirthDate: (carId: string, birthDate: Date) => void;
  setDriverHasCreditCard: (carId: string, hasCreditCard: boolean) => void;
  toggleEquipmentEnquiry: (carId: string, equipmentId: string, quantity: number) => void
  setCarIsBeingUpgraded: (upgradingCar: boolean) => void
  setShowExtraCars: (showExtraCars: boolean) => void
  setShowFilterableCars: (showFilterableCars: boolean) => void
  setCarToBeUpgraded: (carId: string) => void
  setCarToBeSelected: (carId: string) => void
}

type CarRentalFilteringServiceStore = CarRentalFilteringServiceState & CarRentalFilteringServiceStoreActions

export const createCarRentalFilteringServiceStore = (initState: CarRentalFilteringServiceState) =>
  createStore<CarRentalFilteringServiceStore>()(
    immer((set) => ({
      ...initState,
      setCarIsBeingUpgraded(upgradingCar) {
        set((state) => {
          state.isCarBeingUpgraded = upgradingCar
        })
      },
      setCarToBeUpgraded(carId) {
        set((state) => {
          state.upgradedCar = carId
        })
      },
      setCarToBeSelected(carId) {
        set((state) => {
          state.selectedCar = carId
        })
      },
      setShowExtraCars(showExtraCars) {
        set((state) => {
          state.isShowingExtraCars = showExtraCars
        })
      },
      setShowFilterableCars(showFilterableCars) {
        set((state) => {
          state.isShowingFilterableCars = showFilterableCars
        })
      },
      startEditingCar(carId, carState) {
        set(({cars, editingCarsList}) => {
          cars[carId] = carState
          editingCarsList.push(carId)
        })
      },
      endEditingCar(carId) {
        set((state) => {
          state.editingCarsList = state.editingCarsList.filter(
            (id) => id !== carId
          )
        })
      },
      endEditingCarsExcept(carIds) {
        set((state) => {
          state.editingCarsList = state.editingCarsList.filter(
            (id) => carIds.includes(id)
          )
        })
      },
      setDriverId(carId, driverId) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].driverId = driverId
        })
      },
      setDriverBirthDate(carId, birthDate) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].driverBirthDate = birthDate
        })
      },
      setDriverHasCreditCard(carId, hasCreditCard) {
        set(({cars}) => {
          if (!cars[carId]) return
          cars[carId].driverHasCreditCard = hasCreditCard
        })
      },
      toggleEquipmentEnquiry(carId, equipmentId, quantity) {
        set(({cars}) => {
          if (!cars[carId]) return
          const bookedEquipments = cars[carId].bookedEquipmentsWithQuantity

          const equipmentIndex = bookedEquipments.findIndex((eq) => eq.id === equipmentId)

          if (equipmentIndex !== -1) {
            bookedEquipments[equipmentIndex].quantity = quantity
            if (quantity <= 0) {
              bookedEquipments.splice(equipmentIndex, 1)
            }
          } else {
            bookedEquipments.push({id: equipmentId, quantity})
          }
        })
      },
    }))
  );


export const editingCar = (carId: string) => ({ cars }: CarRentalFilteringServiceState) => {
  return cars[carId]
}

export const carIsBeingEdited = (carId: string) => ({ editingCarsList }: CarRentalFilteringServiceState) => {
  return editingCarsList.includes(carId)
}

export const carIsBeingUpgraded = ({ isCarBeingUpgraded }: CarRentalFilteringServiceState) => isCarBeingUpgraded

export const carToBeUpgraded = ({ upgradedCar }: CarRentalFilteringServiceState) => upgradedCar

export const carIsBeingSelected = ({ selectedCar }: CarRentalFilteringServiceState) => Boolean(selectedCar)

export const carToBeSelected = ({ selectedCar }: CarRentalFilteringServiceState) => selectedCar

export const showingExtraCars = ({ isShowingExtraCars }: CarRentalFilteringServiceState) => isShowingExtraCars

export const showingFilterableCars = ({ isShowingFilterableCars }: CarRentalFilteringServiceState) => isShowingFilterableCars

export const carCouldHaveHigherPrice = (carId: string, maxAge: Date, minAge: Date) => ({ cars }: CarRentalFilteringServiceState) => {
  const birthdate = cars[carId]?.driverBirthDate
  if (!birthdate) return false
  return !(birthdate >= maxAge && birthdate <= minAge);
}

export const carRentalServiceStore = createCarRentalFilteringServiceStore({
  cars: {},
  editingCarsList: [],
  isCarBeingUpgraded: false,
  isShowingExtraCars: false,
  isShowingFilterableCars: false,
  upgradedCar: '',
  selectedCar: ''
})

type StoreSelector<T, S> = (state: S) => T
interface UseBoundStore<S> {
  (): S,
  <T>(selector: StoreSelector<T, S>): T
}

export const useCarRentalServiceStore: UseBoundStore<CarRentalFilteringServiceStore> = <T>(selector?: StoreSelector<T, CarRentalFilteringServiceStore>) => {
  const store = useStore(carRentalServiceStore);

  if(!selector) {
    return store;
  }

  return selector(store);
}
